<template>
  <v-container max-width="100%" style="margin-top: 2%">
    <v-toolbar style="margin-bottom: 12px; border-radius: 10px; padding-top: 48px; padding-bottom: 16px">
      <v-container>
        <v-row align="center">
          <v-col cols="12" sm="4" md="3">
            <v-autocomplete
              v-model="idContrato"
              :items="contratos"
              item-text="label"
              item-value="id_contrato"
              :label="$t('subsidyScorecardMelibrasil.contract')"
              clearable
              :loading="loadingContratos"
            />
          </v-col>
          <v-col cols="12" sm="3" md="2">
            <v-select v-model="anoMes" :items="mesesAno" :label="$t('subsidyScorecardMelibrasil.monthYm')" />
          </v-col>
          <v-col cols="12" sm="2" md="2">
            <v-btn color="primary" :disabled="!idContrato || !anoMes" :loading="loading" @click="abrirOuCarregar">
              {{ $t('subsidyScorecardMelibrasil.openAudit') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-toolbar>

    <v-alert v-if="scorecard && scorecard.alerta_rescisao === 1" type="error" dense outlined class="mb-4">
      {{ $t('subsidyScorecardMelibrasil.rescissionAlert') }}
    </v-alert>

    <v-row v-if="scorecard">
      <v-col cols="12" md="3">
        <v-card outlined>
          <v-card-text>
            <div class="text-caption grey--text">{{ $t('subsidyScorecardMelibrasil.pointsScorecard') }}</div>
            <div class="text-h4 primary--text">{{ formatPct(scorecard.pontos_scorecard) }}</div>
            <div class="text-caption mt-1">
              {{ $t('subsidyScorecardMelibrasil.governanceLabel') }}: {{ governanceLabel(scorecard.nivel_governanca) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card outlined>
          <v-card-text>
            <div class="text-caption grey--text">{{ $t('subsidyScorecardMelibrasil.discountApplied') }}</div>
            <div class="text-h5 error--text">{{ currency(scorecard.valor_desconto_aplicado) }}</div>
            <div class="text-caption">
              {{ $t('subsidyScorecardMelibrasil.gross') }}: {{ currency(scorecard.valor_desconto_bruto) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card outlined>
          <v-card-text>
            <v-text-field
              v-model.number="scorecard.valor_mensal_contrato"
              :label="$t('subsidyScorecardMelibrasil.monthlyContractValue')"
              type="number"
              dense
              :disabled="scorecard.fechado === 1"
              @blur="salvarValores"
            />
            <v-text-field
              v-model.number="scorecard.valor_mensal_extras"
              :label="$t('subsidyScorecardMelibrasil.monthlyExtrasValue')"
              type="number"
              dense
              :disabled="scorecard.fechado === 1"
              @blur="salvarValores"
            />
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3" class="d-flex align-center">
        <v-btn
          color="primary"
          class="mr-2"
          :loading="recalculando"
          :disabled="scorecard.fechado === 1"
          @click="recalcular"
        >
          {{ $t('subsidyScorecardMelibrasil.recalculate') }}
        </v-btn>
        <v-btn color="error" outlined :disabled="scorecard.fechado === 1" @click="fecharMes">
          {{ $t('subsidyScorecardMelibrasil.closeMonth') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-tabs v-if="scorecard" v-model="tab" background-color="transparent" class="mt-4">
      <v-tab>{{ $t('subsidyScorecardMelibrasil.tabs.pillarSummary') }}</v-tab>
      <v-tab>{{ $t('subsidyScorecardMelibrasil.tabs.punctuality') }}</v-tab>
      <v-tab>{{ $t('subsidyScorecardMelibrasil.tabs.occurrences') }}</v-tab>
    </v-tabs>

    <v-tabs-items v-if="scorecard" v-model="tab">
      <v-tab-item>
        <merus-data-table
          :headers="headersPilares"
          :items="pilares"
          :loading="loading"
          hide-default-footer
          disable-pagination
        >
          <template v-slot:item.cod_pilar="{ item }">
            {{ pillarLabel(item.cod_pilar) }}
          </template>
          <template v-slot:item.performance_pct="{ item }">
            {{ formatPct(item.performance_pct) }}
          </template>
          <template v-slot:item.valor_desconto="{ item }">
            {{ currency(item.valor_desconto) }}
          </template>
        </merus-data-table>
      </v-tab-item>

      <v-tab-item>
        <v-btn small color="primary" class="mb-3" :loading="loadingPont" @click="importarPontualidade">
          {{ $t('subsidyScorecardMelibrasil.importFromTracker') }}
        </v-btn>
        <merus-data-table
          :headers="headersPont"
          :items="linhasPontualidade"
          :loading="loadingPont"
        >
          <template v-slot:item.performance_pct="{ item }">
            {{ formatPct(item.performance_pct) }}
          </template>
          <template v-slot:item.valor_desconto_total="{ item }">
            {{ currency(item.valor_desconto_total) }}
          </template>
        </merus-data-table>
      </v-tab-item>

      <v-tab-item>
        <v-btn small color="primary" class="mb-3" :disabled="scorecard.fechado === 1" @click="openOcorrencia">
          <v-icon left>mdi-plus</v-icon>
          {{ $t('subsidyScorecardMelibrasil.occurrence') }}
        </v-btn>
        <merus-data-table :headers="headersOcorrencias" :items="ocorrencias" :loading="loading">
          <template v-slot:item.cod_pilar="{ item }">
            {{ pillarLabel(item.cod_pilar) }}
          </template>
          <template v-slot:item.expurgado="{ item }">
            {{ item.expurgado ? $t('system.yes') : $t('system.no') }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-icon small :disabled="scorecard.fechado === 1" @click="editOcorrencia(item)">mdi-pencil</v-icon>
            <v-icon small :disabled="scorecard.fechado === 1" @click="deleteOcorrencia(item)">mdi-delete</v-icon>
          </template>
        </merus-data-table>
      </v-tab-item>
    </v-tabs-items>

    <v-dialog v-model="dialogOcorrencia" max-width="720">
      <v-card>
        <v-card-title>{{ $t('subsidyScorecardMelibrasil.auditOccurrenceTitle') }}</v-card-title>
        <v-card-text>
          <v-form ref="formOcorrencia" v-model="formOcorrenciaValid">
            <v-select
              v-model="editedOcorrencia.cod_pilar"
              :items="pilarOptions"
              item-text="text"
              item-value="value"
              :label="$t('subsidyScorecardMelibrasil.pillar')"
              :rules="[v => !!v || $t('system.messages.requiredField')]"
            />
            <v-text-field
              v-model="editedOcorrencia.tipo_ocorrencia"
              :label="$t('subsidyScorecardMelibrasil.type')"
              :rules="[v => !!v || $t('system.messages.requiredField')]"
            />
            <v-text-field
              v-model="editedOcorrencia.dt_referencia"
              :label="$t('subsidyScorecardMelibrasil.referenceDate')"
              type="datetime-local"
            />
            <v-text-field
              v-model="editedOcorrencia.dt_solicitacao"
              :label="$t('subsidyScorecardMelibrasil.requestDate')"
              type="datetime-local"
            />
            <v-text-field
              v-model="editedOcorrencia.dt_prevista"
              :label="$t('subsidyScorecardMelibrasil.expectedDate')"
              type="datetime-local"
            />
            <v-text-field
              v-model="editedOcorrencia.dt_realizada"
              :label="$t('subsidyScorecardMelibrasil.completedDate')"
              type="datetime-local"
            />
            <v-text-field
              v-model.number="editedOcorrencia.dias_atraso"
              :label="$t('subsidyScorecardMelibrasil.delayDays')"
              type="number"
            />
            <v-text-field
              v-model.number="editedOcorrencia.performance_informada_pct"
              :label="$t('subsidyScorecardMelibrasil.performancePctSummary')"
              type="number"
            />
            <v-text-field
              v-model.number="editedOcorrencia.quantidade"
              :label="$t('subsidyScorecardMelibrasil.quantity')"
              type="number"
            />
            <v-checkbox
              v-model="editedOcorrencia.expurgado"
              :label="$t('subsidyScorecardMelibrasil.expungedFortuitous')"
            />
            <v-text-field v-model="editedOcorrencia.motivo_expurgo" :label="$t('subsidyScorecardMelibrasil.expungedReason')" />
            <v-textarea v-model="editedOcorrencia.observacao" :label="$t('subsidyScorecardMelibrasil.observation')" rows="2" />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogOcorrencia = false">{{ $t('button.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveOcorrencia">{{ $t('button.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import apiController from "@/utils/apiController";
import EventBus from "@/utils/eventBus";

const PILAR_OPTION_KEYS = [
  "HE_SINERGIAS",
  "ALTERACAO_ROTA",
  "VALIDACAO_CANDIDATOS",
  "INCLUSAO_LINHAS",
  "COMUNICACAO_CENTRAL",
  "SEGURANCA_ACIDENTES",
];

function emptyOcorrencia() {
  return {
    cod_pilar: "HE_SINERGIAS",
    tipo_ocorrencia: "",
    dt_referencia: null,
    dt_solicitacao: null,
    dt_prevista: null,
    dt_realizada: null,
    dias_atraso: null,
    performance_informada_pct: null,
    quantidade: 1,
    expurgado: false,
    motivo_expurgo: "",
    observacao: "",
  };
}

export default {
  name: "SubsidioScorecardMelibrasil",
  data() {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth()).padStart(2, "0");
    return {
      contratos: [],
      idContrato: null,
      anoMes: `${y}-${m}`,
      mesesAno: [],
      scorecard: null,
      pilares: [],
      ocorrencias: [],
      linhasPontualidade: [],
      loading: false,
      loadingContratos: false,
      loadingPont: false,
      recalculando: false,
      tab: 0,
      dialogOcorrencia: false,
      editedOcorrencia: emptyOcorrencia(),
      editingOcorrenciaId: null,
      formOcorrenciaValid: false,
    };
  },
  computed: {
    pilarOptions() {
      return PILAR_OPTION_KEYS.map((value) => ({
        value,
        text: this.pillarLabel(value),
      }));
    },
    headersPilares() {
      const t = (k) => this.$t(`subsidyScorecardMelibrasil.headers.${k}`);
      return [
        { text: t("pillar"), value: "cod_pilar" },
        { text: t("slaMetaPct"), value: "sla_meta_pct" },
        { text: t("performancePct"), value: "performance_pct" },
        { text: t("discountPct"), value: "pct_desconto_aplicado" },
        { text: t("discountValue"), value: "valor_desconto" },
      ];
    },
    headersPont() {
      const t = (k) => this.$t(`subsidyScorecardMelibrasil.headers.${k}`);
      return [
        { text: t("line"), value: "cod_linha" },
        { text: t("segments"), value: "n_trechos" },
        { text: t("onTime"), value: "n_trechos_pontuais" },
        { text: t("performancePct"), value: "performance_pct" },
        { text: t("discount"), value: "valor_desconto_total" },
      ];
    },
    headersOcorrencias() {
      const t = (k) => this.$t(`subsidyScorecardMelibrasil.headers.${k}`);
      return [
        { text: t("pillar"), value: "cod_pilar" },
        { text: t("type"), value: "tipo_ocorrencia" },
        { text: t("reference"), value: "dt_referencia" },
        { text: t("expunged"), value: "expurgado" },
        { text: t("actions"), value: "actions", sortable: false },
      ];
    },
  },
  created() {
    this.buildMesesAno();
    this.loadContratos();
  },
  methods: {
    pillarLabel(cod) {
      if (!cod) return "-";
      const key = `subsidyScorecardMelibrasil.pillars.${cod}`;
      return this.$te(key) ? this.$t(key) : cod;
    },
    governanceLabel(nivel) {
      if (!nivel) return "-";
      const key = `subsidyScorecardMelibrasil.governanceLevels.${nivel}`;
      return this.$te(key) ? this.$t(key) : nivel;
    },
    buildMesesAno() {
      const items = [];
      const now = new Date();
      for (let i = 0; i < 24; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        items.push(ym);
      }
      this.mesesAno = items;
    },
    currency(v) {
      const localeCode = this.$t("contract.localeCode") || "pt-BR";
      const currencyCode = this.$t("contract.currencyCode") || "BRL";
      return new Intl.NumberFormat(localeCode, {
        style: "currency",
        currency: currencyCode,
      }).format(Number(v) || 0);
    },
    formatPct(v) {
      if (v == null) return "-";
      return `${Math.round(Number(v) * 100) / 100}%`;
    },
    monthBounds(anoMes) {
      const [y, m] = String(anoMes).split("-").map(Number);
      const last = new Date(y, m, 0).getDate();
      const pad = (n) => String(n).padStart(2, "0");
      return {
        dti: `${y}-${pad(m)}-01 00:00:00`,
        dtf: `${y}-${pad(m)}-${pad(last)} 23:59:59`,
      };
    },
    async loadContratos() {
      this.loadingContratos = true;
      try {
        const resp = await apiController.request(apiController.endpoints.subsidioGetallContratossubsidio, { silent: true });
        const list = Array.isArray(resp.data) ? resp.data : [];
        const ativos = list.filter((c) => c.ativo === 1 || c.ativo === true);
        const controles = ativos.filter((c) => (c.tipo_contrato || "OPERACIONAL") === "CONTROLE");
        const fonte = controles.length > 0 ? controles : ativos;
        this.contratos = fonte.map((c) => {
          const qtd = Number(c.qtd_filhos) || 0;
          const suffix = qtd > 0 ? ` (${qtd})` : "";
          return {
            ...c,
            label: `${c.cod_contrato || c.id_contrato} — ${c.descricao || ""}${suffix}`,
          };
        });
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      } finally {
        this.loadingContratos = false;
      }
    },
    applyPayload(data) {
      if (!data) return;
      this.scorecard = data.scorecard;
      this.pilares = data.pilares || [];
      this.ocorrencias = data.ocorrencias || [];
      const pontPilar = this.pilares.find((p) => p.cod_pilar === "PONTUALIDADE");
      const pont = pontPilar && (pontPilar.detalhe || pontPilar.detalhe_json);
      this.linhasPontualidade = (pont && pont.linhas) || [];
    },
    async abrirOuCarregar() {
      this.loading = true;
      try {
        const listResp = await apiController.request(apiController.endpoints.subsidioMelibrasilScorecard, {
          query: { id_contrato: this.idContrato, ano_mes: this.anoMes },
          silent: true,
        });
        const existing = Array.isArray(listResp.data) ? listResp.data[0] : null;
        if (existing && existing.id_scorecard) {
          await this.carregar(existing.id_scorecard);
          return;
        }
        const { dti, dtf } = this.monthBounds(this.anoMes);
        const createResp = await apiController.request(apiController.endpoints.subsidioMelibrasilInsertScorecard, {
          data: {
            id_contrato: this.idContrato,
            ano_mes: this.anoMes,
            dti,
            dtf,
            valor_mensal_contrato: 0,
            valor_mensal_extras: 0,
          },
          silent: true,
        });
        this.applyPayload(createResp.data);
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      } finally {
        this.loading = false;
      }
    },
    async carregar(id) {
      const resp = await apiController.request(apiController.endpoints.subsidioMelibrasilScorecardById, {
        params: { id },
        silent: true,
      });
      this.applyPayload(resp.data);
    },
    async salvarValores() {
      if (!this.scorecard || this.scorecard.fechado === 1) return;
      try {
        await apiController.request(apiController.endpoints.subsidioMelibrasilUpdateScorecard, {
          params: { id: this.scorecard.id_scorecard },
          data: {
            valor_mensal_contrato: this.scorecard.valor_mensal_contrato,
            valor_mensal_extras: this.scorecard.valor_mensal_extras,
          },
          silent: true,
        });
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      }
    },
    async recalcular() {
      if (!this.scorecard) return;
      this.recalculando = true;
      try {
        const resp = await apiController.request(apiController.endpoints.subsidioMelibrasilRecalcularScorecard, {
          params: { id: this.scorecard.id_scorecard },
          data: {},
          silent: true,
        });
        this.applyPayload(resp.data);
        EventBus.$emit("show-alert", {
          mensagem: this.$t("subsidyScorecardMelibrasil.recalculatedSuccess"),
          tipo: "success",
        });
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      } finally {
        this.recalculando = false;
      }
    },
    async fecharMes() {
      if (!this.scorecard) return;
      try {
        const resp = await apiController.request(apiController.endpoints.subsidioMelibrasilFecharScorecard, {
          params: { id: this.scorecard.id_scorecard },
          data: {},
          silent: true,
        });
        this.applyPayload(resp.data);
        EventBus.$emit("show-alert", {
          mensagem: this.$t("subsidyScorecardMelibrasil.monthClosedSuccess"),
          tipo: "success",
        });
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      }
    },
    async importarPontualidade() {
      if (!this.scorecard) return;
      this.loadingPont = true;
      try {
        const { dti, dtf } = this.monthBounds(this.anoMes);
        await apiController.request(apiController.endpoints.subsidioMelibrasilGetPontualidadeporlinha, {
          data: { dti, dtf },
          silent: true,
        });
        await this.recalcular();
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      } finally {
        this.loadingPont = false;
      }
    },
    openOcorrencia() {
      this.editingOcorrenciaId = null;
      this.editedOcorrencia = emptyOcorrencia();
      this.dialogOcorrencia = true;
    },
    editOcorrencia(item) {
      this.editingOcorrenciaId = item.id_ocorrencia;
      this.editedOcorrencia = {
        cod_pilar: item.cod_pilar,
        tipo_ocorrencia: item.tipo_ocorrencia,
        dt_referencia: item.dt_referencia,
        dt_solicitacao: item.dt_solicitacao,
        dt_prevista: item.dt_prevista,
        dt_realizada: item.dt_realizada,
        dias_atraso: item.dias_atraso,
        performance_informada_pct: item.performance_informada_pct,
        quantidade: item.quantidade,
        expurgado: item.expurgado === 1,
        motivo_expurgo: item.motivo_expurgo,
        observacao: item.observacao,
      };
      this.dialogOcorrencia = true;
    },
    async saveOcorrencia() {
      if (!this.$refs.formOcorrencia.validate()) return;
      const id = this.scorecard.id_scorecard;
      const payload = { ...this.editedOcorrencia, expurgado: this.editedOcorrencia.expurgado ? 1 : 0 };
      try {
        if (this.editingOcorrenciaId) {
          await apiController.request(apiController.endpoints.subsidioMelibrasilUpdateOcorrencia, {
            params: { id, idOcorrencia: this.editingOcorrenciaId },
            data: payload,
            silent: true,
          });
        } else {
          await apiController.request(apiController.endpoints.subsidioMelibrasilInsertOcorrencia, {
            params: { id },
            data: payload,
            silent: true,
          });
        }
        this.dialogOcorrencia = false;
        await this.recalcular();
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      }
    },
    async deleteOcorrencia(item) {
      try {
        await apiController.request(apiController.endpoints.subsidioMelibrasilDeleteOcorrencia, {
          params: { id: this.scorecard.id_scorecard, idOcorrencia: item.id_ocorrencia },
          silent: true,
        });
        await this.recalcular();
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      }
    },
  },
};
</script>
